import { useState, type ReactNode } from "react";
import { Alert, Box, IconButton, InputAdornment, Link, Stack, TextField, Tooltip, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import { AutocompleteCodeInput } from "@/components/common/fields/AutocompleteCodeInput";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { ConditionWindowFields } from "@/components/common/inputs/ConditionWindowFields";
import { FormSection } from "@/components/common/inputs/FormSection";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import Button from "@/components/ui/Button";
import { HintIcon } from "@/components/ui/mui/HintIcon";
import { ButtonStyleType, ColorType, ConfirmLabelType, UIComponentSize } from "@/types/shared/uiComponents";
import type { ConditionWindowDefaults } from "@/utils/helpers/conditionWindowDefaults";
import { conditionWindowsOutsideDefaultsWarning } from "@/utils/validations/laseredConditionValidation";
import { formatDate } from "@/utils/formatters/dateFormatter";
import {
  canSetExpectedTrigger,
  conditionDisplayState,
  expectedTriggerNotApplied,
  hasLiveExpectedTrigger,
  strandedExpectedTriggerWarning,
} from "@/utils/helpers/expectedTrigger";
import { ResourceName, type LaseredCondition } from "@/types";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useActivePartner } from "@/hooks/useActivePartner";
import { sameTerms, type ConditionDraft, type LaseredConditionDraftsApi } from "../hooks/useLaseredConditionDrafts";
import { CancelExpectedTriggerDialog, SetExpectedTriggerDialog } from "./ExpectedTriggerDialogs";
import { ExpectedTriggerSummary } from "./ExpectedTriggerSummary";

interface LaseredConditionsEditorProps {
  drafts: LaseredConditionDraftsApi;
  defaults: ConditionWindowDefaults;
  /**
   * Set when the section must be disabled (contract-excluded claimant). Rows
   * become read-only and no rows can be added, but saved rows can still be
   * removed (delete-only), so conditions entered before an exclusion are not stuck.
   */
  disabledReason?: string;
  /** Inline, non-blocking: conditions exist but are inert (contract-lasered claimant). */
  inertWarning?: string;
  /**
   * A save is in flight: rows, removals and Add are all off until it settles, so a
   * row can't be removed from the dialog after its write was sent (!4880 thread 30679).
   */
  locked?: boolean;
  /**
   * Inside a section that already has the "Conditional Lasers" title (the
   * claimant Deductibles tab): show the lead only, not a second titled section.
   */
  embedded?: boolean;
  /**
   * Offer the expected-trigger actions on saved rows (#7361; the policy page's
   * laser dialog). They save at once, not with the form.
   */
  expectedTrigger?: {
    /** Contract-lasered or excluded claimant: setting is not offered (AC7); cancelling still is. */
    contractClaimant?: boolean;
    /** After a set or cancel saved (the caller starts the recompute refresh). */
    onSaved?: () => void;
  };
}

const INERT_HINT = "The claimant has a contract laser or exclusion on this policy; contract terms take precedence.";

const LEAD =
  "These change the claimant's specific deductible when a claim line matches their ICD (code or sub-code) or CPT codes inside both windows. One can raise or lower the deductible; the most recent date of service wins, from that date for all in-window spend.";

/**
 * State chip for a saved condition (Armed / Triggered on date / Expected on
 * date / Overdue / Inert). `compact` drops the claim-line link and the in-force
 * note (confirmations).
 */
export const LaseredConditionStateChip = ({
  condition,
  compact,
}: {
  condition?: LaseredCondition;
  compact?: boolean;
}) => <StateChip draft={{ server: condition } as ConditionDraft} compact={compact} />;

const StateChip = ({ draft, compact }: { draft: ConditionDraft; compact?: boolean }) => {
  const s = draft.server;
  if (!s) return <StatusChip size={UIComponentSize.Small} label="New" />;
  if (s.lasered_condition_trigger_state === "inert") {
    // The reason sits in a focusable HintIcon, not a tooltip on the chip (UX review #19).
    return (
      <Stack direction="row" spacing={0.25} alignItems="center" component="span">
        <StatusChip size={UIComponentSize.Small} label="Inert" />
        <HintIcon hint={INERT_HINT} variant="info" />
      </Stack>
    );
  }
  const state = conditionDisplayState(s);
  if (state === "overdue") {
    // Past its review-by date with no claim line: a review holds the deductible (#7361).
    return (
      <Stack direction="row" spacing={1} alignItems="center" component="span" data-testid="condition-overdue">
        <StatusChip size={UIComponentSize.Small} color={ColorType.Error} label="Overdue" />
        {s.expected_trigger?.expected_date && (
          <Typography variant="caption" color="text.secondary">
            expected on {formatDate(s.expected_trigger.expected_date)}
          </Typography>
        )}
      </Stack>
    );
  }
  if (state === "triggered" || state === "expected") {
    const expected = state === "expected";
    return (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        component="span"
        data-testid={expected ? "condition-expected" : "condition-triggered"}
      >
        <StatusChip
          size={UIComponentSize.Small}
          color={s.is_effective ? ColorType.Warning : ColorType.Default}
          label={expected ? "Expected" : "Triggered"}
        />
        {s.trigger_date && (
          <Typography variant="caption" color="text.secondary">
            on {formatDate(s.trigger_date)}
          </Typography>
        )}
        {!compact && s.triggered_by_claim_item_id && (
          // New tab: the editor and the review dialog hold unsaved work (UX review #7).
          <Link
            variant="caption"
            href={`/#/claim-items/${s.triggered_by_claim_item_id}/show`}
            target="_blank"
            rel="noopener"
          >
            View claim line
          </Link>
        )}
        {!compact && s.is_effective && (
          <Typography variant="caption" color="text.secondary">
            (in force)
          </Typography>
        )}
      </Stack>
    );
  }
  return <StatusChip size={UIComponentSize.Small} color={ColorType.Info} label="Armed" />;
};

/**
 * One window warning for the editor, not one per row (UX review #12): the rows
 * whose incurred or paid window extends beyond the policy dates.
 */
const windowRangeWarning = (
  drafts: ConditionDraft[],
  defaults: ConditionWindowDefaults,
  rowErrors: Record<string, string>
) => {
  const outside = drafts
    .filter((d) => !rowErrors[d.key])
    .map((d) => ({ d, warning: conditionWindowsOutsideDefaultsWarning(d, defaults.windows) }))
    .filter(({ warning }) => !!warning);
  if (outside.length === 0) return "";
  if (outside.length === 1) {
    const { d, warning } = outside[0];
    return `${d.lasered_condition_name.trim() || "This conditional laser"}: ${warning}`;
  }
  const names = outside.map(({ d }) => d.lasered_condition_name.trim() || "Unnamed").join(", ");
  return `${names}: windows extend beyond the policy dates. That is allowed (run-in lookbacks, carrier terms); confirm they match the laser notice.`;
};

/**
 * Repeatable conditional-laser rows (#3511). Each row: name, ICD and CPT codes,
 * a deductible and the four windows (#7344). Triggered rows stay editable,
 * because triggers are re-derived and can reverse.
 */
export const LaseredConditionsEditor = ({
  drafts,
  defaults,
  disabledReason,
  inertWarning,
  embedded,
  expectedTrigger,
  locked = false,
}: LaseredConditionsEditorProps) => {
  const [confirmRemove, setConfirmRemove] = useState<ConditionDraft | null>(null);
  // Expected-trigger dialogs (#7361): the row's key, so the dialog reads its current server state.
  const [setTargetKey, setSetTargetKey] = useState<string | null>(null);
  const [cancelTargetKey, setCancelTargetKey] = useState<string | null>(null);
  const setTarget = drafts.drafts.find((d) => d.key === setTargetKey);
  const cancelTarget = drafts.drafts.find((d) => d.key === cancelTargetKey);
  const claimantId = drafts.claimantId;
  const expectedTriggerSaved = (key: string) => (saved: LaseredCondition) => {
    drafts.applyServer(key, saved);
    expectedTrigger?.onSaved?.();
  };
  const disabled = !!disabledReason || locked;
  const rangeWarning = windowRangeWarning(drafts.drafts, defaults, drafts.rowErrors);

  // A titled section of its own, so it is not mistaken for the straight laser's
  // reference-only condition codes (UX review #3).
  const wrap = (content: ReactNode) =>
    embedded ? (
      <Box data-testid="lasered-conditions-editor">
        <Typography variant="body2" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          {LEAD}
        </Typography>
        {content}
      </Box>
    ) : (
      <Box data-testid="lasered-conditions-editor">
        <FormSection title="Conditional Lasers" subtitle={LEAD}>
          {content}
        </FormSection>
      </Box>
    );

  // The API gates these on claimant access (set / edit = PUT, claimants:update;
  // cancel = POST, claimants:create) and on the read-only partner guard
  // (ADR-035). Offering them to someone it refuses only ends in a 403 (!4881
  // thread 30547).
  const { canEdit: canEditClaimant, canCreate: canCreateClaimant } = useResourcePermissions(ResourceName.CLAIMANTS);
  const { readOnly: partnerReadOnly } = useActivePartner(!!expectedTrigger);
  const canSetExpected = canEditClaimant && !partnerReadOnly;
  const canCancelExpected = canCreateClaimant && !partnerReadOnly;
  const noExpectedAccess = partnerReadOnly
    ? "Expected triggers can't be changed under this partner (read-only). Switch to the claimant's partner."
    : "Your role can't change expected triggers.";
  const unsavedFirst = "Save this conditional laser's changes first.";

  // Set is offered on a saved, armed row of a non-contract claimant (AC7).
  const offerSet = (d: ConditionDraft) =>
    !!expectedTrigger &&
    !disabledReason &&
    !!claimantId &&
    canSetExpectedTrigger(d.server, !!expectedTrigger.contractClaimant);

  // Edit / cancel on a live expected trigger. Edit replaces it (PUT), so it is
  // not offered where setting is not; cancelling always is.
  const liveActions = (d: ConditionDraft) => {
    if (!expectedTrigger || !claimantId || !hasLiveExpectedTrigger(d.server)) return undefined;
    const name = d.server?.lasered_condition_name ?? "conditional laser";
    const busy = drafts.isPersisting || locked;
    // Overdue: settled in its deductible review, so a decision is recorded; the
    // API refuses the PUT while that review is open (PO 2026-10-14). Not when it is
    // also outside the incurred window: the resolver ignores it, so no review opens,
    // and Edit is how it is moved back inside (!4881 thread 30686).
    const overdue = d.server?.expected_trigger?.state === "overdue" && !expectedTriggerNotApplied(d.server);
    const editBlocked = !canSetExpected
      ? noExpectedAccess
      : overdue
        ? "Overdue: settle it in the deductible review (keep it with a new review-by date, or the standing deductible)."
        : !sameTerms(d)
          ? unsavedFirst
          : "";
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        {!expectedTrigger.contractClaimant && !disabledReason && !d.server?.triggered_by_claim_item_id && (
          <Tooltip title={editBlocked || "Edit expected trigger"}>
            <span>
              <IconButton
                size="small"
                aria-label={`Edit expected trigger for ${name}`}
                disabled={!!editBlocked || busy}
                onClick={() => setSetTargetKey(d.key)}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {/* Disabled while the row has unsaved changes, as Set and Edit (#7361 AC8; !4881 thread 30546). */}
        <Tooltip title={!canCancelExpected ? noExpectedAccess : sameTerms(d) ? "" : unsavedFirst}>
          <span>
            <Button
              variant={ButtonStyleType.Text}
              size={UIComponentSize.Small}
              color={ColorType.Inherit}
              disabled={!canCancelExpected || !sameTerms(d) || busy}
              onClick={() => setCancelTargetKey(d.key)}
              aria-label={`Cancel expected trigger for ${name}`}
            >
              Cancel expected trigger
            </Button>
          </span>
        </Tooltip>
      </Stack>
    );
  };

  const requestRemove = (d: ConditionDraft) => {
    if (d.server?.lasered_condition_trigger_state === "triggered") {
      setConfirmRemove(d);
      return;
    }
    drafts.remove(d.key);
  };

  return wrap(
    <>
      {drafts.loadFailed && (
        <Alert severity="error" role="alert" sx={{ mb: 1.5, fontSize: "0.8125rem" }}>
          {drafts.error}
        </Alert>
      )}
      {disabledReason && (
        <Alert severity="info" variant="outlined" sx={{ mb: 1.5, fontSize: "0.8125rem" }}>
          {disabledReason}
          {drafts.drafts.length > 0 ? " Existing conditional lasers can still be removed." : ""}
        </Alert>
      )}
      {!disabledReason && inertWarning && drafts.drafts.length > 0 && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 1.5, fontSize: "0.8125rem" }}>
          {inertWarning}
        </Alert>
      )}

      <Stack spacing={2}>
        {drafts.drafts.map((d, i) => {
          const rowError = drafts.rowErrors[d.key];
          // Edited windows that leave the live expected trigger uncovered: said here, before Save (#7361).
          const strandedWarning = strandedExpectedTriggerWarning(d.server, d);
          return (
            <Box
              key={d.key}
              sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}
              data-testid={`condition-row-${i}`}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <StateChip draft={d} />
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {offerSet(d) && (
                    <Tooltip title={!canSetExpected ? noExpectedAccess : sameTerms(d) ? "" : unsavedFirst}>
                      <span>
                        <Button
                          variant={ButtonStyleType.Text}
                          size={UIComponentSize.Small}
                          startIcon={<EventOutlinedIcon />}
                          disabled={!canSetExpected || !sameTerms(d) || drafts.isPersisting || locked}
                          onClick={() => setSetTargetKey(d.key)}
                          aria-label={`Set expected trigger for ${d.server?.lasered_condition_name}`}
                          data-testid={`set-expected-trigger-${i}`}
                        >
                          Set expected trigger
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title="Remove conditional laser">
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`Remove ${d.lasered_condition_name || "conditional laser"}`}
                        disabled={locked}
                        onClick={() => requestRemove(d)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
              {d.server?.expected_trigger && (
                <Box sx={{ mb: 1.5 }}>
                  <ExpectedTriggerSummary condition={d.server} actions={liveActions(d)} />
                </Box>
              )}
              <Stack spacing={1.5}>
                <Stack direction="row" spacing={2}>
                  <TextField
                    label="Condition Name"
                    required
                    fullWidth
                    size="small"
                    disabled={disabled}
                    value={d.lasered_condition_name}
                    onChange={(e) => drafts.update(d.key, { lasered_condition_name: e.target.value })}
                    slotProps={{ htmlInput: { "data-testid": `condition-name-${i}` } }}
                  />
                  <TextField
                    label="Conditional Deductible"
                    type="number"
                    required
                    fullWidth
                    size="small"
                    disabled={disabled}
                    value={d.amt}
                    onChange={(e) => drafts.update(d.key, { amt: e.target.value })}
                    slotProps={{
                      input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
                      htmlInput: { "data-testid": `condition-amt-${i}` },
                    }}
                  />
                </Stack>
                <AutocompleteCodeInput
                  codeType="icd"
                  value={d.icd_codes}
                  onChange={(codes) => drafts.update(d.key, { icd_codes: codes })}
                  disabled={disabled}
                  label="ICD Codes"
                  placeholder="e.g., N18 matches N18.6"
                />
                <AutocompleteCodeInput
                  codeType="cpt"
                  value={d.cpt_codes}
                  onChange={(codes) => drafts.update(d.key, { cpt_codes: codes })}
                  disabled={disabled}
                  label="CPT Codes (exact)"
                  placeholder="Search by code or description"
                />
                <ConditionWindowFields
                  idPrefix={`condition-${i}`}
                  value={d}
                  onChange={(w) => drafts.update(d.key, w)}
                  defaults={defaults}
                  disabled={disabled}
                  error={rowError}
                  hideRangeWarning
                />
                {strandedWarning && (
                  <Alert
                    severity="warning"
                    variant="outlined"
                    sx={{ fontSize: "0.8125rem" }}
                    data-testid={`condition-expected-trigger-warning-${i}`}
                  >
                    {strandedWarning}
                  </Alert>
                )}
              </Stack>
            </Box>
          );
        })}
      </Stack>

      {!disabledReason && rangeWarning && (
        <Alert
          severity="info"
          variant="outlined"
          sx={{ mt: 1.5, fontSize: "0.8125rem" }}
          data-testid="condition-window-warning"
        >
          {rangeWarning}
        </Alert>
      )}

      <Button
        variant={ButtonStyleType.Text}
        size={UIComponentSize.Small}
        startIcon={<AddIcon />}
        onClick={drafts.add}
        disabled={disabled}
        sx={{ mt: 1.5 }}
        data-testid="add-condition"
      >
        Add conditional laser
      </Button>

      {claimantId && (
        <>
          <SetExpectedTriggerDialog
            open={!!setTarget}
            claimantId={claimantId}
            condition={setTarget?.server}
            onClose={() => setSetTargetKey(null)}
            onSaved={expectedTriggerSaved(setTarget?.key ?? "")}
            onStale={drafts.reload}
          />
          <CancelExpectedTriggerDialog
            open={!!cancelTarget}
            claimantId={claimantId}
            condition={cancelTarget?.server}
            onClose={() => setCancelTargetKey(null)}
            onSaved={expectedTriggerSaved(cancelTarget?.key ?? "")}
            onStale={drafts.reload}
          />
        </>
      )}

      <ConfirmationDialog
        open={!!confirmRemove}
        title="Remove a triggered conditional laser?"
        message={`"${confirmRemove?.lasered_condition_name ?? ""}" ${
          confirmRemove?.server?.triggered_by_claim_item_id || !hasLiveExpectedTrigger(confirmRemove?.server)
            ? "has triggered"
            : "has an expected trigger"
        }. Removing it re-derives the claimant's specific deductible from the remaining terms when you save, and stop-loss claims are recalculated.`}
        confirmLabel={ConfirmLabelType.Remove}
        confirmColor={ColorType.Error}
        onCancel={() => setConfirmRemove(null)}
        onConfirm={() => {
          if (confirmRemove) drafts.remove(confirmRemove.key);
          setConfirmRemove(null);
        }}
      />
    </>
  );
};
